import React, { useState } from 'react';
import { Brain, Clock, AlertTriangle, Package, Droplet, Mountain, Cloud, TrendingUp, ChevronDown, ChevronUp, Download } from 'lucide-react';
import '../styles/AIAnalysis.css';

const AIAnalysis = ({ analysis, onDownloadGPX, enhancedGpxFilename }) => {
  const [expandedSections, setExpandedSections] = useState({
    keyPoints: true,
    safety: true,
    equipment: true,
    waterFood: true,
    scenic: false,
    strava: false,
    weather: true,
    alternatives: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  if (!analysis) {
    return (
      <div className="ai-analysis-container">
        <div className="no-analysis">
          <Brain size={48} />
          <p>No hi ha anàlisi disponible</p>
        </div>
      </div>
    );
  }

  const getDifficultyColor = (difficulty) => {
    const colors = { Fàcil: '#22c55e', Mitjà: '#f59e0b', Difícil: '#ef4444', Expert: '#991b1b' };
    return colors[difficulty] || '#667eea';
  };

  return (
    <div className="ai-analysis-container fade-in">
      <div className="analysis-header">
        <div className="header-icon"><Brain size={32} /></div>
        <div className="header-content">
          <h2>Anàlisi Intel·ligent amb IA</h2>
          <p>Powered by Google Gemini AI + OSM + Strava</p>
        </div>
      </div>

      <div className="highlights-grid">
        <div className="highlight-card" style={{ borderColor: getDifficultyColor(analysis.difficulty_rating) }}>
          <Mountain size={24} />
          <div className="highlight-content">
            <span className="highlight-label">Dificultat</span>
            <span className="highlight-value" style={{ color: getDifficultyColor(analysis.difficulty_rating) }}>
              {analysis.difficulty_rating}
            </span>
          </div>
        </div>
        <div className="highlight-card">
          <Clock size={24} />
          <div className="highlight-content">
            <span className="highlight-label">Temps Estimat</span>
            <span className="highlight-value">{analysis.estimated_time}</span>
          </div>
        </div>
      </div>

      {analysis.key_points && analysis.key_points.length > 0 && (
        <Section title="Punts Clau de la Ruta" icon={<TrendingUp size={20} />} isExpanded={expandedSections.keyPoints} onToggle={() => toggleSection('keyPoints')}>
          <div className="key-points-list">
            {analysis.key_points.map((point, index) => (
              <div key={index} className="key-point-item">
                <span className="key-point-km">KM {point.km}</span>
                <span className="key-point-description">{point.description}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {analysis.safety_warnings && analysis.safety_warnings.length > 0 && (
        <Section title="Advertències de Seguretat" icon={<AlertTriangle size={20} />} isExpanded={expandedSections.safety} onToggle={() => toggleSection('safety')} variant="warning">
          <ul className="warning-list">
            {analysis.safety_warnings.map((warning, index) => (<li key={index}>{warning}</li>))}
          </ul>
        </Section>
      )}

      {analysis.recommended_equipment && analysis.recommended_equipment.length > 0 && (
        <Section title="Equipament Recomanat" icon={<Package size={20} />} isExpanded={expandedSections.equipment} onToggle={() => toggleSection('equipment')}>
          <div className="equipment-grid">
            {analysis.recommended_equipment.map((item, index) => (
              <div key={index} className="equipment-item">
                <span className="equipment-bullet">✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {analysis.water_food_stops && analysis.water_food_stops.length > 0 && (
        <Section title="Aigua i Avituallament" icon={<Droplet size={20} />} isExpanded={expandedSections.waterFood} onToggle={() => toggleSection('waterFood')}>
          <div className="water-food-list">
            {analysis.water_food_stops.map((stop, index) => (
              <div key={index} className="water-food-item">
                <div className="stop-header">
                  <span className="stop-km">KM {stop.km}</span>
                  <span className="stop-type">{stop.type}</span>
                </div>
                <div className="stop-details">
                  <strong>{stop.name}</strong>
                  {stop.notes && <p>{stop.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {analysis.scenic_highlights && analysis.scenic_highlights.length > 0 && (
        <Section title="Vistes i Miradors" icon={<Mountain size={20} />} isExpanded={expandedSections.scenic} onToggle={() => toggleSection('scenic')}>
          <ul className="scenic-list">
            {analysis.scenic_highlights.map((highlight, index) => (<li key={index}>{highlight}</li>))}
          </ul>
        </Section>
      )}

      {analysis.strava_comparison && (
        <Section title="Comparació amb Strava" icon={<img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24'%3E%3Cpath fill='%23fc4c02' d='M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0L0 17.944h6.121'/%3E%3C/svg%3E" alt="Strava" width="20" height="20" />} isExpanded={expandedSections.strava} onToggle={() => toggleSection('strava')}>
          <p className="strava-text">{analysis.strava_comparison}</p>
        </Section>
      )}

      {analysis.weather_recommendations && analysis.weather_recommendations.length > 0 && (
        <Section title="Recomanacions Meteorològiques" icon={<Cloud size={20} />} isExpanded={expandedSections.weather} onToggle={() => toggleSection('weather')}>
          <ul className="weather-list">
            {analysis.weather_recommendations.map((rec, index) => (<li key={index}>{rec}</li>))}
          </ul>
        </Section>
      )}

      {analysis.alternative_routes && analysis.alternative_routes.length > 0 && (
        <Section title="Rutes Alternatives" icon={<TrendingUp size={20} />} isExpanded={expandedSections.alternatives} onToggle={() => toggleSection('alternatives')}>
          <ul className="alternatives-list">
            {analysis.alternative_routes.map((alt, index) => (<li key={index}>{alt}</li>))}
          </ul>
        </Section>
      )}

      {onDownloadGPX && (
        <button className="download-gpx-button" onClick={onDownloadGPX}>
          <Download size={20} />
          Descarregar GPX Millorat amb POIs
        </button>
      )}
    </div>
  );
};

const Section = ({ title, icon, children, isExpanded, onToggle, variant }) => {
  return (
    <div className={`analysis-section ${variant || ''}`}>
      <div className="section-header" onClick={onToggle}>
        <div className="section-title">
          {icon}
          <h3>{title}</h3>
        </div>
        <button className="toggle-button">
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>
      {isExpanded && <div className="section-content">{children}</div>}
    </div>
  );
};

export default AIAnalysis;